import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import useApiClient from '~/src/hooks/useApiClient';
import { snackbarMessageSelector } from '~/src/Snackbar/store';
import { apiKeyListState, newApiKeyState, pendingApiKeyState } from '../store';

export const API_KEY_URL = '/authentication/api-keys';

interface CreateApiKeyParams {
  name: string;
  scopes: string[];
  expiresAt?: string | null;
}

const useApiKeys = () => {
  const { t } = useTranslation('develop');
  const apiClient = useApiClient();
  const setPending = useSetRecoilState(pendingApiKeyState);
  const setSnackbarMessage = useSetRecoilState(snackbarMessageSelector);
  const setNewApiKey = useSetRecoilState(newApiKeyState);
  const setApiKeyList = useSetRecoilState(apiKeyListState);

  const create = async (params: CreateApiKeyParams) => {
    setPending(true);
    try {
      const { data } = await apiClient.post(API_KEY_URL, params);
      setNewApiKey(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSnackbarMessage({
        message: t('apiKey.errors.create'),
        severity: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  const read = async () => {
    setPending(true);
    try {
      const { data } = await apiClient.get(API_KEY_URL);
      setApiKeyList(data.length ? data : null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSnackbarMessage({
        message: t('apiKey.errors.get'),
        severity: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  const revoke = async (id: string) => {
    setPending(true);
    try {
      await apiClient.delete(`${API_KEY_URL}/${id}`);
      setApiKeyList((list) => (list ? list.filter((item) => item._id !== id) : null));
      setSnackbarMessage({
        message: t('apiKey.success.revoke'),
        severity: 'success',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSnackbarMessage({
        message: t('apiKey.errors.revoke'),
        severity: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  return { create, read, revoke };
};

export default useApiKeys;
